import React, { useState, useCallback, useEffect } from "react";
import { withFormik } from 'formik';
import * as Yup from "yup";
import moment from "moment"
import { useTranslation } from 'react-i18next';
import { useStoreState } from 'easy-peasy';

import { Button, Typography, Spinner } from "stories/components";
import { Container, Row, Col } from "stories/layout";
import { Input, Radio, DatePicker } from "stories/forms";
import { useNotification } from "stories/components/Notification"
import { useAppDialog } from "components/AppDialog/useAppDialog"
import CurrencySelect from "components/Forms/CurrencySelect/CurrencySelect"

import {
  httpGetTransaction,
  httpGetTransactions,
  httpCreateInterestTransaction,
  httpUpdateTransaction,
  httpDeleteTransaction,
} from "services/transactions"
import { INTEREST_TYPE, INTEREST_CAPITALIZED_TYPE } from "utils/interestTransactions"

// Korkokirjaus lainalle. Sama lomake kummallekin tapahtumatyypille:
// 33 Korko (rahana maksettu) ja 34 Korko, pääomitettu (ei kassavirtaa).
// Kirjaus tallentuu suoraan valitun salkun tapahtumaksi; erä ja saldo ratkaistaan palvelimella.

const defaultValues = {
  id: null,
  type: INTEREST_TYPE,
  date_transaction: null,
  portfolio: "",
  price_total: "",
  notes: "",
}

const toNoonUtc = (date) => {
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
  return local.toISOString().split('T')[0] + " 12:00:00Z";
}

const InterestTransactionEdit = (props) => {
  const {
    values, touched, setTouched, errors, setErrors, handleChange, handleBlur, handleSubmit,
    setFieldValue, isSubmitting, validateForm,
  } = props;
  const { t, objectId, currency_label, holdingPortfolios, portfolios } = props;
  const { dialog, confirm } = useAppDialog();

  const getFormikInputProps = useCallback(
    name => ({
      name,
      value: values[name],
      error: Boolean(touched[name] && errors[name]),
      errorText: touched[name] ? errors[name] : "",
      onBlur: handleBlur,
      onChange: handleChange
    }),
    [values, errors, touched, handleBlur, handleChange]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    validateForm().then(res => {
      if (Object.keys(res).length === 0) {
        handleSubmit()
      }
      else {
        setTouched(res);
        setErrors({
          ...res,
          general: t("Please check the form errors", "Tarkista lomakkeen virheet")
        })
      }
    })
  }

  const handleCancel = () => {
    props.handleReject()
  }

  const handleDelete = () => {
    confirm(t("are_you_sure_window_confirm", "Oletko varma?")).then((result) => {
      if (result) {
        httpDeleteTransaction(objectId).then(() => {
          props.notify({ title: t("interest_notify_title", "Korkokirjaus"), message: t("Deleted", "Poistettu") })
          props.handleResolve()
        })
      }
    });
  }

  // Salkkuvalikossa vain salkut, joissa lainaa on ostettu. Jos tietoa ei ole, näytetään kaikki
  // (palvelin tarkistaa joka tapauksessa, että salkussa on lainaa maksupäivänä).
  const portfolioOptions = holdingPortfolios && holdingPortfolios.length > 0
    ? portfolios.filter(p => holdingPortfolios.includes(p.id))
    : portfolios;

  return (
    <>
      {dialog}
      <Row>
        <Col className="mb-3">
          <Typography variant="small" className="font-weight-bold mb-1">
            {t("interest_edit_type", "Tapahtumatyyppi")} <span className="text-danger">*</span>
          </Typography>
          <Radio
            {...getFormikInputProps("type")}
            inline
            checkedChoice={String(values?.type)}
            choices={[
              { id: "interest_type_cash", label: t("interest_type_cash", "Korko"), value: String(INTEREST_TYPE) },
              { id: "interest_type_capitalized", label: t("interest_type_capitalized", "Korko, pääomitettu"), value: String(INTEREST_CAPITALIZED_TYPE) },
            ]}
            onChange={(e) => setFieldValue("type", parseInt(e.target.value, 10))}
          />
        </Col>
      </Row>
      <Row>
        <Col className="mb-3">
          <DatePicker
            {...getFormikInputProps("date_transaction")}
            label={t("interest_edit_pay_date", "Maksupäivä")}
            className="form-control"
            selected={values?.date_transaction}
            name="date_transaction"
            onChange={(date) => setFieldValue("date_transaction", date)}
            showYearDropdown
            dateFormat="d.M.yyyy"
            value={values?.date_transaction}
            required
          />
        </Col>
        <Col className="mb-3">
          <CurrencySelect
            {...getFormikInputProps("currency_label")}
            label={t("PrivateInvestmentFundEdit_currency", "Valuutta")}
            value={currency_label}
            required
            disabled={true}
          />
        </Col>
      </Row>
      <Row>
        <Col className="mb-3">
          <Input
            {...getFormikInputProps("portfolio")}
            label={t("interest_edit_portfolio", "Salkku")}
            type="select"
            disabled={objectId ? true : false}
            required
          >
            <option value="">{t("interest_edit_select_portfolio", "Valitse salkku")}</option>
            {portfolioOptions && portfolioOptions.map(obj => (
              <option key={obj.id} value={obj.id}>{obj.name} ({obj?.currency_label})</option>
            ))}
          </Input>
        </Col>
        <Col className="mb-3">
          <Input
            {...getFormikInputProps("price_total")}
            label={t("interest_edit_amount", "Korkomaksu yhteensä")}
            type="number"
            min={0}
            step={"0.01"}
            onWheel={(e) => e.target.blur()}
            placeholder={t("0")}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col className="mb-3">
          <Input
            {...getFormikInputProps("notes")}
            label={t("interest_edit_notes", "Selite")}
            type="text"
            placeholder={t("interest_edit_notes_placeholder", "esim. Korkojakso 1–6/2026")}
          />
        </Col>
      </Row>
      <Row>
        <Col className="mb-3">
          <Typography variant="small" className="text-muted">
            {values?.type == INTEREST_CAPITALIZED_TYPE
              ? t("interest_edit_info_capitalized", "Pääomitettu korko kirjataan tuotoksi, mutta sitä ei lasketa kassavirtaan.")
              : t("interest_edit_info", "Kirjaus tallentuu suoraan valitun salkun tapahtumiin.")}
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col className="mb-3">
          <Button onClick={handleCancel} variant="secondary" className="sk-modal-dismiss">{t("Close", "Sulje")}</Button>
        </Col>
        {values?.id ? (
          <Col className="mb-3 text-center">
            <Button onClick={handleDelete} variant="danger">{t("Delete", "Poista")}</Button>
          </Col>
        ) : null}
        <Col className="mb-3 text-right">
          <Button variant="primary" disabled={isSubmitting} onClick={onSubmit}>
            {t("Save", "Tallenna")}
          </Button>
          {Boolean(errors["general"]) && <Typography className="text-danger">{errors["general"]}</Typography>}
        </Col>
      </Row>
    </>
  );
}

const InterestTransactionEditFormik = withFormik({
  enableReinitialize: true,
  validateOnMount: true,
  mapPropsToValues: props => {
    const { preSetValues } = props;
    return preSetValues ? preSetValues : { ...defaultValues };
  },
  validationSchema: props => {
    const { t } = props;
    const required = t("The field is required");
    return Yup.object().shape({
      type: Yup.number().oneOf([INTEREST_TYPE, INTEREST_CAPITALIZED_TYPE]).required(required),
      date_transaction: Yup.date().required(required).nullable(),
      portfolio: Yup.string().required(required),
      price_total: Yup.number().typeError(required).moreThan(0, t("interest_edit_amount_positive", "Korkomaksun on oltava suurempi kuin nolla")).required(required),
    });
  },
  handleSubmit: (values, { setSubmitting, setErrors, props }) => {
    const { t, related_model_id, related_model_type, currency_label } = props;
    const amount = parseFloat(String(values?.price_total).replace(",", "."));
    const date_transaction = toNoonUtc(values.date_transaction);

    const handleApiError = (error) => {
      setSubmitting(false);
      const data = error?.data;
      if (data?.detail) {
        setErrors({ general: data.detail });
        return;
      }
      if (data && typeof data === 'object') {
        const fieldErrors = {};
        Object.entries(data).forEach(([key, value]) => {
          if (key === 'errorCode') return;
          const text = Array.isArray(value) ? value.join(' ') : String(value);
          if (key === 'non_field_errors' || key === 'general') {
            fieldErrors.general = text;
          } else {
            fieldErrors[key] = text;
          }
        });
        // Lomakkeen omat kentät näyttävät virheensä itse; muut (esim. related_model_id) nostetaan yleisvirheeksi
        const formFields = ["type", "date_transaction", "portfolio", "price_total", "notes"];
        const unknown = Object.keys(fieldErrors).filter(key => key !== "general" && !formFields.includes(key));
        if (!fieldErrors.general && unknown.length > 0) {
          fieldErrors.general = unknown.map(key => fieldErrors[key]).join(' ');
        }
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          return;
        }
      }
      setErrors({ general: t("Unknown error. Please try again later or contact support.") });
    };

    if (values?.id) {
      // Muokkaus: tyyppi, päivä, summa ja selite. cash_flow seuraa tyyppiä palvelimella.
      httpUpdateTransaction(values.id, {
        type: values.type,
        date_transaction: date_transaction,
        date_settlement: date_transaction,
        price_total: amount,
        value_total: amount,
        notes: values.notes || null,
      }).then(res => {
        setSubmitting(false);
        props.notify({ title: t("interest_notify_title", "Korkokirjaus"), message: t("Updated") })
        props.handleResolve()
      }, handleApiError)
    }
    else {
      httpCreateInterestTransaction({
        portfolio: values.portfolio,
        related_model_id: related_model_id,
        related_model_type: related_model_type,
        type: values.type,
        date_transaction: date_transaction,
        price_total: amount,
        currency_label: currency_label,
        notes: values.notes || null,
      }).then(res => {
        setSubmitting(false);
        props.notify({ title: t("interest_notify_title", "Korkokirjaus"), message: t("Created") })
        props.handleResolve()
      }, handleApiError)
    }
  },
  displayName: "InterestTransactionForm"
})(InterestTransactionEdit)

const InterestTransactionEditView = ({ history, objectId, modalOpen, related_model_id, related_model_type, ...rest }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [preSetValues, setPreSetValues] = useState();
  const [holdingPortfolios, setHoldingPortfolios] = useState([]);
  const { notify } = useNotification();
  const portfolios = useStoreState((state) => state.portfolio?.portfolios);

  const getObject = (id) => {
    setLoading(true)
    httpGetTransaction(id).then(res => {
      setPreSetValues({
        ...defaultValues,
        id: res?.data?.id,
        type: res?.data?.type,
        portfolio: res?.data?.portfolio?.id ?? res?.data?.portfolio,
        price_total: res?.data?.price_total ? parseFloat(res?.data?.price_total) : "",
        notes: res?.data?.notes || "",
        date_transaction: res?.data?.date_transaction ? moment(res?.data?.date_transaction).toDate() : null,
      });
    }).finally(() => {
      setLoading(false);
    })
  }

  // Salkut, joissa lainaa on ostettu (oletusvalinta, jos vain yksi)
  const getHoldingPortfolios = () => {
    if (!related_model_id) {
      return;
    }
    httpGetTransactions({ related_model_type: related_model_type, related_model_id: related_model_id, type: 2, limit: 500 }).then(res => {
      const rows = res?.data?.results || res?.data || [];
      // listan serializer palauttaa salkun objektina, yksittäinen tapahtuma id:nä
      const ids = [...new Set(rows.map(row => (row?.portfolio?.id ?? row?.portfolio)))].filter(id => id != null);
      setHoldingPortfolios(ids);
      if (!objectId) {
        setPreSetValues(s => ({
          ...(s || defaultValues),
          portfolio: ids.length === 1 ? String(ids[0]) : (s?.portfolio || ""),
        }));
      }
    })
  }

  useEffect(() => {
    if (objectId && parseInt(objectId) > -1) {
      getObject(objectId)
    }
    else {
      setPreSetValues({ ...defaultValues });
    }
    getHoldingPortfolios()
  }, [objectId, modalOpen])

  if (loading) {
    return (
      <Container fluid>
        <Spinner />
      </Container>
    )
  }

  return (
    <InterestTransactionEditFormik
      t={t}
      history={history}
      preSetValues={preSetValues}
      notify={notify}
      objectId={objectId}
      related_model_id={related_model_id}
      related_model_type={related_model_type}
      holdingPortfolios={holdingPortfolios}
      portfolios={portfolios || []}
      {...rest}
    />
  )
}

export default InterestTransactionEditView;
